import { PaymentMethodEnum } from 'config/constants/payment'
import { Network, Token } from 'config/types'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import Column from 'layout/Components/Column'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Text from 'UIKit/Text'
import ContractInfo from 'views/Payment/components/ContractInfo'
import { PaymentPageEnums } from 'views/Payment/Menu'
import ExtraInfo, { ExtraInfoItem } from '../../../components/ExtraInfo'
import SelectedBonusItem from '../../components/SelectedBonusItem'
import SelectedNetworkItem from '../../components/SelectedNetworkItem'
import {
  PaymentCopyButton,
  PaymentInputContainer,
  PaymentLabel,
  PaymentQrCode,
  PaymentStroke,
} from '../../components/styles'
import { DepositModeEnums, DepositQrCodeControls, useDepositControl } from '../../hooks/useDeposit'
import { IBonusControl } from '../../types'

interface DepositByQRCodeProps {
  selectedNetwork: Network
  selectedToken: Token
  bonusControl: IBonusControl

  onSelectToken?: (currency: Token) => void
}

const DepositByQRCode: React.FC<DepositByQRCodeProps> = ({
  selectedNetwork,
  selectedToken,
  bonusControl,
  onSelectToken,
}) => {
  const { t } = useTranslation()
  const {
    depositControl: {
      data: { addressReceiveFund, qrCode, memo, minDepositAmount },
      status: { isFetchingDepositInfo },
    },
  } = useDepositControl<DepositQrCodeControls>({
    selectedToken,
    selectedNetwork,
    mode: DepositModeEnums.QR,
  })

  const { isMobile } = useMatchBreakpoints()

  return (
    <>
      <PaymentQrCode isLoading={isFetchingDepositInfo} value={qrCode} selectedToken={selectedToken} />

      {isMobile && (
        <>
          <Column gap="var(--pm-space-label, 8px)">
            <PaymentLabel>
              <Trans>Network</Trans>
            </PaymentLabel>
            <SelectedNetworkItem
              selectedNetwork={selectedNetwork}
              selectedToken={selectedToken}
              onSelectToken={onSelectToken}
              from={PaymentPageEnums.Deposit}
            />
          </Column>
        </>
      )}

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentLabel>
          <Trans>Deposit Address</Trans>
        </PaymentLabel>
        <PaymentInputContainer height="auto" gap="20px">
          <Text small bold wordBreak="break-all" color="var(--pm-color)">
            {addressReceiveFund}
          </Text>
          <PaymentCopyButton disabled={!addressReceiveFund} text={addressReceiveFund} />
        </PaymentInputContainer>
      </Column>

      {memo && (
        <Column gap="var(--pm-space-label, 8px)">
          <PaymentLabel>
            <Trans>Destination Tag</Trans>
          </PaymentLabel>
          <PaymentInputContainer>
            <Text small bold wordBreak="break-all" color="var(--pm-color)">
              {memo}
            </Text>
            <PaymentCopyButton text={memo} />
          </PaymentInputContainer>
          <Text fontSize="var(--pm-text-note-size)" color="warning" textAlign="left">
            {t('Important! If you make a transaction without a Destination tag, your funds will be lost!')}
          </Text>
        </Column>
      )}

      {bonusControl.bonuses.length > 0 && (
        <>
          <PaymentStroke />
          <SelectedBonusItem inputAmount={null} bonusControl={bonusControl} selectedToken={selectedToken} />
        </>
      )}

      {selectedNetwork && (
        <>
          <PaymentStroke />

          <ExtraInfo>
            <ExtraInfoItem title="Minimum Deposit">
              {minDepositAmount ? (
                <>
                  {minDepositAmount} {selectedToken.code}
                </>
              ) : (
                '-'
              )}
            </ExtraInfoItem>

            <ExtraInfoItem hide={selectedToken.isNative || !selectedToken.address} title="Contract Information">
              <ContractInfo from={PaymentMethodEnum.Deposit} token={selectedToken} />
            </ExtraInfoItem>
          </ExtraInfo>
        </>
      )}
    </>
  )
}

export default DepositByQRCode
